import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ThemeLoader } from "@/components/theme/ThemeLoader";
import { Theme, isValidTheme } from "@/components/theme/types";

export type { Theme } from "@/components/theme/types";

export function useTheme() {
  const [theme, setThemeState] = useState<Theme | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Set up callback for ThemeLoader to update our state
  useEffect(() => {
    ThemeLoader.setThemeChangeCallback((newTheme: Theme) => {
      console.log("Theme callback received:", newTheme);
      setThemeState(newTheme);
      setIsLoading(false);
    });
  }, []);

  // Initialize theme based on user authentication status
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        console.log("Initializing theme...");
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          console.log("User found, loading user theme");
          // Don't wait for DB load, ThemeLoader handles it
          await ThemeLoader.loadUserTheme(user.id);
        } else {
          console.log("No user, using generic theme");
          // For unauthenticated users, use generic localStorage
          const stored = localStorage.getItem("theme");
          if (stored && isValidTheme(stored)) {
            setThemeState(stored as Theme);
            ThemeLoader.applyThemeToDOM(stored as Theme);
          } else {
            setThemeState("light");
            ThemeLoader.applyThemeToDOM("light");
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error initializing theme:", error);
        setThemeState("light");
        ThemeLoader.applyThemeToDOM("light");
        setIsLoading(false);
      }
    };

    initializeTheme();
  }, []);

  const setTheme = async (newTheme: Theme) => {
    try {
      console.log("Setting new theme:", newTheme);
      setThemeState(newTheme);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // For authenticated users, use ThemeLoader to handle everything
        await ThemeLoader.saveTheme(user.id, newTheme);
      } else {
        // For unauthenticated users, handle manually
        localStorage.setItem("theme", newTheme);
        ThemeLoader.applyThemeToDOM(newTheme);
      }
    } catch (error) {
      console.error("Error updating theme:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo actualizar el tema.",
      });
    }
  };

  return { 
    theme: theme || "light", 
    setTheme, 
    isLoading: isLoading && theme === null 
  };
}
